<script setup>
import { ref, reactive, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import InboxHolidaysAPI from 'dashboard/api/inboxHolidays';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t } = useI18n();
const store = useStore();

const holidays = ref([]);
const isLoading = ref(false);
const isSubmitting = ref(false);
const isSavingGreeting = ref(false);
const formDialog = ref(null);
const deleteDialog = ref(null);
const editingHoliday = ref(null);
const deletingHoliday = ref(null);
const holidayGreeting = ref(props.inbox.holiday_greeting || '');

watch(
  () => props.inbox.holiday_greeting,
  val => {
    holidayGreeting.value = val || '';
  }
);

const form = reactive({
  name: '',
  date: '',
  recurring: true,
});

const resetForm = () => {
  form.name = '';
  form.date = '';
  form.recurring = true;
};

const fetchHolidays = async () => {
  isLoading.value = true;
  try {
    const { data } = await InboxHolidaysAPI.list(props.inbox.id);
    holidays.value = data;
  } catch {
    useAlert(t('INBOX_MGMT.HOLIDAYS.API.FETCH_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

onMounted(fetchHolidays);

const openCreateForm = () => {
  editingHoliday.value = null;
  resetForm();
  formDialog.value.open();
};

const openEditForm = holiday => {
  editingHoliday.value = holiday;
  form.name = holiday.name;
  form.date = holiday.date;
  form.recurring = holiday.recurring;
  formDialog.value.open();
};

const closeForm = () => {
  formDialog.value.close();
};

const saveHoliday = async () => {
  if (!form.name || !form.date) return;
  isSubmitting.value = true;
  try {
    if (editingHoliday.value) {
      const { data } = await InboxHolidaysAPI.update(
        props.inbox.id,
        editingHoliday.value.id,
        { name: form.name, date: form.date, recurring: form.recurring }
      );
      const idx = holidays.value.findIndex(h => h.id === data.id);
      if (idx !== -1) holidays.value[idx] = data;
      useAlert(t('INBOX_MGMT.HOLIDAYS.API.UPDATE_SUCCESS'));
    } else {
      const { data } = await InboxHolidaysAPI.create(props.inbox.id, {
        name: form.name,
        date: form.date,
        recurring: form.recurring,
      });
      holidays.value.push(data);
      useAlert(t('INBOX_MGMT.HOLIDAYS.API.CREATE_SUCCESS'));
    }
    closeForm();
  } catch {
    if (editingHoliday.value) {
      useAlert(t('INBOX_MGMT.HOLIDAYS.API.UPDATE_ERROR'));
    } else {
      useAlert(t('INBOX_MGMT.HOLIDAYS.API.CREATE_ERROR'));
    }
  } finally {
    isSubmitting.value = false;
  }
};

const openDeleteDialog = holiday => {
  deletingHoliday.value = holiday;
  deleteDialog.value.open();
};

const confirmDelete = async () => {
  if (!deletingHoliday.value) return;
  try {
    await InboxHolidaysAPI.delete(props.inbox.id, deletingHoliday.value.id);
    holidays.value = holidays.value.filter(
      h => h.id !== deletingHoliday.value.id
    );
    useAlert(t('INBOX_MGMT.HOLIDAYS.API.DELETE_SUCCESS'));
  } catch {
    useAlert(t('INBOX_MGMT.HOLIDAYS.API.DELETE_ERROR'));
  } finally {
    deletingHoliday.value = null;
    deleteDialog.value.close();
  }
};

const formatHolidayDate = (dateStr, recurring) => {
  const date = parseISO(dateStr);
  return format(date, recurring ? 'MMM dd' : 'MMM dd, yyyy');
};

const saveHolidayGreeting = async () => {
  isSavingGreeting.value = true;
  try {
    await store.dispatch('inboxes/updateInbox', {
      id: props.inbox.id,
      formData: false,
      holiday_greeting: holidayGreeting.value,
      channel: {},
    });
    useAlert(t('INBOX_MGMT.HOLIDAYS.GREETING.SUCCESS'));
  } catch {
    useAlert(t('INBOX_MGMT.HOLIDAYS.GREETING.ERROR'));
  } finally {
    isSavingGreeting.value = false;
  }
};
</script>

<template>
  <div class="mx-6 max-w-4xl">
    <div class="mb-6">
      <h2 class="text-heading-2 text-n-slate-12">
        {{ t('INBOX_MGMT.HOLIDAYS.TITLE') }}
      </h2>
      <p class="text-body-main text-n-slate-11 mt-1">
        {{ t('INBOX_MGMT.HOLIDAYS.DESCRIPTION') }}
      </p>
    </div>

    <div
      class="outline outline-1 -outline-offset-1 outline-n-weak rounded-xl p-4 mb-6 flex flex-col gap-3"
    >
      <div>
        <p class="text-body-sm font-medium text-n-slate-12">
          {{ t('INBOX_MGMT.HOLIDAYS.GREETING.LABEL') }}
        </p>
        <p class="text-body-sm text-n-slate-11 mt-0.5">
          {{ t('INBOX_MGMT.HOLIDAYS.GREETING.HINT') }}
        </p>
      </div>
      <WootMessageEditor
        v-model="holidayGreeting"
        is-format-mode
        :enable-suggestions="false"
        :focus-on-mount="false"
        :placeholder="t('INBOX_MGMT.HOLIDAYS.GREETING.PLACEHOLDER')"
      />
      <div class="flex justify-end">
        <NextButton
          :label="t('INBOX_MGMT.HOLIDAYS.GREETING.SAVE')"
          :is-loading="isSavingGreeting"
          @click="saveHolidayGreeting"
        />
      </div>
    </div>

    <div class="flex items-center justify-between mb-4">
      <p class="text-body-sm font-medium text-n-slate-12">
        {{ t('INBOX_MGMT.HOLIDAYS.LIST_TITLE') }}
      </p>
      <NextButton
        :label="t('INBOX_MGMT.HOLIDAYS.ADD_HOLIDAY')"
        icon="i-ph-plus"
        @click="openCreateForm"
      />
    </div>

    <div
      v-if="isLoading"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <span class="i-ph-spinner-gap animate-spin size-6" />
    </div>

    <div
      v-else-if="holidays.length === 0"
      class="flex flex-col items-center justify-center py-16 text-center"
    >
      <span class="i-ph-calendar-blank size-12 text-n-slate-9 mb-3" />
      <p class="text-body-main text-n-slate-11">
        {{ t('INBOX_MGMT.HOLIDAYS.EMPTY_STATE') }}
      </p>
    </div>

    <div
      v-else
      class="outline outline-1 -outline-offset-1 outline-n-weak rounded-xl overflow-hidden"
    >
      <table class="w-full text-sm">
        <thead>
          <tr class="border-b border-n-weak bg-n-alpha-1">
            <th
              class="text-left px-4 py-3 text-body-sm font-medium text-n-slate-11"
            >
              {{ t('INBOX_MGMT.HOLIDAYS.TABLE.NAME') }}
            </th>
            <th
              class="text-left px-4 py-3 text-body-sm font-medium text-n-slate-11"
            >
              {{ t('INBOX_MGMT.HOLIDAYS.TABLE.DATE') }}
            </th>
            <th
              class="text-left px-4 py-3 text-body-sm font-medium text-n-slate-11"
            >
              {{ t('INBOX_MGMT.HOLIDAYS.TABLE.RECURRING') }}
            </th>
            <th
              class="text-right px-4 py-3 text-body-sm font-medium text-n-slate-11"
            >
              {{ t('INBOX_MGMT.HOLIDAYS.TABLE.ACTIONS') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="holiday in holidays"
            :key="holiday.id"
            class="border-b border-n-weak last:border-0"
          >
            <td class="px-4 py-3 text-n-slate-12">{{ holiday.name }}</td>
            <td class="px-4 py-3 text-n-slate-11">
              {{ formatHolidayDate(holiday.date, holiday.recurring) }}
            </td>
            <td class="px-4 py-3">
              <span
                class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                :class="
                  holiday.recurring
                    ? 'bg-n-teal-3 text-n-teal-11'
                    : 'bg-n-slate-3 text-n-slate-11'
                "
              >
                {{
                  holiday.recurring
                    ? t('INBOX_MGMT.HOLIDAYS.RECURRING_YES')
                    : t('INBOX_MGMT.HOLIDAYS.RECURRING_NO')
                }}
              </span>
            </td>
            <td class="px-4 py-3 text-right">
              <div class="flex items-center justify-end gap-2">
                <NextButton
                  ghost
                  slate
                  size="sm"
                  icon="i-ph-pencil-simple"
                  @click="openEditForm(holiday)"
                />
                <NextButton
                  ghost
                  ruby
                  size="sm"
                  icon="i-ph-trash"
                  @click="openDeleteDialog(holiday)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Create / Edit Dialog -->
    <Dialog
      ref="formDialog"
      :title="
        editingHoliday
          ? t('INBOX_MGMT.HOLIDAYS.FORM.EDIT_TITLE')
          : t('INBOX_MGMT.HOLIDAYS.FORM.CREATE_TITLE')
      "
      :confirm-button-label="t('INBOX_MGMT.HOLIDAYS.FORM.SAVE')"
      :cancel-button-label="t('INBOX_MGMT.HOLIDAYS.FORM.CANCEL')"
      :is-loading="isSubmitting"
      :disable-confirm-button="!form.name || !form.date"
      @confirm="saveHoliday"
    >
      <div class="flex flex-col gap-4">
        <Input
          v-model="form.name"
          :label="t('INBOX_MGMT.HOLIDAYS.FORM.NAME_LABEL')"
          :placeholder="t('INBOX_MGMT.HOLIDAYS.FORM.NAME_PLACEHOLDER')"
        />
        <div class="flex flex-col gap-1">
          <Input
            v-model="form.date"
            type="date"
            :label="t('INBOX_MGMT.HOLIDAYS.FORM.DATE_LABEL')"
          />
          <p v-if="form.recurring" class="text-xs text-n-slate-11">
            {{ t('INBOX_MGMT.HOLIDAYS.FORM.DATE_HINT_RECURRING') }}
          </p>
        </div>
        <div class="flex items-center justify-between">
          <span class="text-body-main text-n-slate-12">
            {{ t('INBOX_MGMT.HOLIDAYS.FORM.RECURRING_LABEL') }}
          </span>
          <Switch v-model="form.recurring" />
        </div>
      </div>
    </Dialog>

    <!-- Delete Confirmation Dialog -->
    <Dialog
      ref="deleteDialog"
      type="alert"
      :title="t('INBOX_MGMT.HOLIDAYS.DELETE.CONFIRM_TITLE')"
      :description="t('INBOX_MGMT.HOLIDAYS.DELETE.CONFIRM_MESSAGE')"
      :confirm-button-label="t('INBOX_MGMT.HOLIDAYS.DELETE.CONFIRM_BUTTON')"
      :cancel-button-label="t('INBOX_MGMT.HOLIDAYS.DELETE.CANCEL_BUTTON')"
      @confirm="confirmDelete"
      @close="deletingHoliday = null"
    />
  </div>
</template>
